import { formatInr } from './selfRegularization.js';
import { formatShortDate } from './format.js';

/**
 * Wording and shapes for the Wallet: the all-source money (GET /surepass-usage/spend) and what is
 * left of the Surepass credits (GET /surepass-usage/credits).
 *
 * Every rupee figure that comes from the ledger already has GST in it — that is what left the
 * Surepass wallet — and the rate-card price beside it never does. Nothing here computes money; it
 * only says it.
 */

const num = (n) => Number(n || 0).toLocaleString('en-IN');
const DIGITS = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** One entry may be at most this many rupees — the same guard the API applies. */
export const MAX_CREDIT_INR = 10_000_000;

/** Wallet money, always to the paisa so it lines up with the Surepass console. */
export function formatMoney(n) {
  const value = Number(n || 0);
  return `${value < 0 ? '-' : ''}₹${DIGITS.format(Math.abs(value))}`;
}

/** One colour per source, in the campaign-os categorical family, so a source reads the same everywhere. */
export const SOURCE_COLOR = {
  registration: '#0A9188',
  verification_run: '#B0872F',
  document_read: '#3E7C8C',
  check_script: '#B7AD96',
  other: '#949E9A',
  earlier: '#12A99B',
};

/** "₹21.00 + ₹3.78 GST" — said wherever a tax-inclusive figure is shown; null when there is no GST in it. */
export function gstCaption(beforeInr, gstInr) {
  const gst = Number(gstInr || 0);
  if (!(gst > 0)) return null;
  return `${formatMoney(beforeInr)} + ${formatMoney(gst)} GST`;
}

/** The inline note naming API groups with answered calls and no price, and the keys that would price them. */
export function notPricedNote(spend) {
  const groups = spend?.not_priced ?? [];
  if (!groups.length) return null;
  const labels = groups.map((g) => g.label).join(', ');
  const keys = groups.map((g) => g.env_key).filter(Boolean);
  const calls = groups.reduce((n, g) => n + (g.billable_calls || 0), 0);
  const set = keys.length ? ` — set ${keys.join(', ')}` : '';
  const counted = calls === 1 ? 'Its 1 answered call is' : `Their ${num(calls)} answered calls are`;
  return `Not priced yet: ${labels}${set}. ${counted} counted but left out of the rupee totals.`;
}

/** Donut data for spend by source: rupees once anything is priced, calls until then. */
export function sourceSlices(bySource) {
  const list = bySource ?? [];
  if (!list.length) return { unit: 'calls', data: [] };
  const priced = list.some((x) => Number(x.cost_inr) > 0);
  return {
    unit: priced ? 'cost' : 'calls',
    data: list.map((x) => ({ name: x.label, value: priced ? x.cost_inr : x.calls, source: x.source })),
  };
}

/** BarList items for spend by API group: rupees with GST, with the calls and the rate-card price beside them. */
export function groupBars(byGroup) {
  return (byGroup ?? []).map((g) => {
    const calls = `${num(g.calls)} ${g.calls === 1 ? 'call' : 'calls'}${g.free ? `, ${num(g.free)} free` : ''}`;
    // Google bills Vision itself, so only Surepass groups carry GST on top of the card price.
    const taxed = g.group !== 'google_vision';
    const price = g.priced ? `${formatInr(g.price_inr)}${taxed ? ' + GST' : ''} each` : 'not priced';
    return { label: g.label, value: g.cost_inr, meta: `${calls} · ${price}` };
  });
}

/** What one call took from the wallet, in words, with the GST spelled out behind it. */
export function callCost(call) {
  if (!call.billable) return { label: 'Free', tone: 'free', title: 'Refused or failed — the provider did not charge it' };
  if (call.cost_inr === null || call.cost_inr === undefined) {
    return { label: 'Not priced', tone: 'unpriced', title: 'Answered, but no price is set for this API yet' };
  }
  const gst = Number(call.gst_inr || 0);
  const before = Math.round((Number(call.cost_inr) - gst) * 100) / 100;
  const title =
    gst > 0
      ? `${formatMoney(before)} + ${formatMoney(gst)} GST`
      : call.group === 'google_vision'
        ? 'Billed by Google, not from your Surepass credits'
        : null;
  return { label: formatInr(call.cost_inr), tone: 'cost', title };
}

const OUTCOME_LABEL = {
  success: 'Answered',
  not_found: 'Not found',
  forbidden: 'Not enabled',
  auth: 'Refused',
  upstream: 'Provider error',
  timeout: 'Timed out',
  network: 'Network error',
  bad_response: 'Unreadable answer',
};

export const outcomeLabel = (outcome) => OUTCOME_LABEL[outcome] || outcome;

const CONSOLE_NOTE = 'Matches the Surepass console when every paid call is recorded — calls made outside this app are not.';
const TOP_UP_LINE = 'Top up on the Surepass console, then record it here.';

/**
 * The credits hero: what is left, how much of the credits that is, and the three figures beside it.
 * `balance` is the API's balance block, or null when nothing has been recorded yet.
 */
export function creditsHero(balance) {
  if (!balance) {
    return {
      state: 'empty',
      tone: 'teal',
      title: 'Set your Surepass balance to see what is left',
      value: null,
      progressPct: 0,
      figures: [],
      caption: 'Enter what the Surepass console shows right now. Every call recorded after that is subtracted from it, at your rate card plus GST.',
      note: CONSOLE_NOTE,
      alert: null,
      warning: null,
    };
  }

  const state = balance.negative ? 'negative' : balance.low ? 'low' : 'ok';
  const tone = state === 'negative' ? 'coral' : state === 'low' ? 'amber' : 'teal';
  const days = balance.days_left;
  const runway =
    days === null || days === undefined
      ? { value: '—', sub: 'No spend in the last 30 days' }
      : { value: `≈ ${num(days)} ${days === 1 ? 'day' : 'days'}`, sub: "at the last 30 days' pace" };

  const alert =
    state === 'negative'
      ? { tone: 'coral', text: `Calls have cost more than the credits recorded. ${TOP_UP_LINE}` }
      : state === 'low'
        ? { tone: 'amber', text: `Running low${days ? ` — about ${num(days)} days left at this pace` : ''}. ${TOP_UP_LINE}` }
        : null;

  const unpriced = Number(balance.unpriced_calls_since || 0);
  const gst = Number(balance.gst_percent || 0);

  return {
    state,
    tone,
    title: 'Remaining',
    value: formatMoney(balance.remaining_inr),
    progressPct: Math.min(100, Math.max(0, Number(balance.used_pct || 0))),
    figures: [
      { key: 'credited', label: 'Total credited', value: formatMoney(balance.total_credited_inr), sub: null },
      {
        key: 'spent',
        label: `Spent since ${formatShortDate(balance.since)}`,
        value: formatMoney(balance.spent_inr),
        sub: Number(balance.spent_gst_inr || 0) > 0 ? `incl. ${formatMoney(balance.spent_gst_inr)} GST` : null,
      },
      { key: 'runway', label: 'Runway', value: runway.value, sub: runway.sub },
    ],
    caption: `Estimated from every recorded call at your rate card${gst > 0 ? ` plus ${gst}% GST` : ''} — the Surepass console is the final word.`,
    note: CONSOLE_NOTE,
    alert,
    warning:
      unpriced > 0
        ? `${num(unpriced)} paid call${unpriced === 1 ? '' : 's'} since then ${unpriced === 1 ? 'has' : 'have'} no price yet — ${unpriced === 1 ? 'it is' : 'they are'} not in this figure.`
        : null,
  };
}

/** What an entry did to the balance: an opening balance sets it, a top-up adds, an adjustment moves it either way. */
export function creditAmount(entry) {
  const value = Number(entry?.amount_inr || 0);
  if (entry?.kind === 'opening_balance') return formatMoney(value);
  return `${value < 0 ? '−' : '+'}${formatMoney(Math.abs(value))}`;
}

/** Rows for the credit history list; the date is left as it came, for the page to show in local time. */
export function creditRows(entries) {
  return (entries ?? []).map((e) => {
    const value = Number(e.amount_inr || 0);
    return {
      id: e.id,
      kind: e.kind,
      kind_label: e.kind_label,
      effective_at: e.effective_at,
      amount: creditAmount(e),
      tone: e.kind === 'opening_balance' ? 'ink' : value < 0 ? 'coral' : 'teal',
      note: e.note ?? null,
      who: e.created_by?.name ?? 'Unknown',
      counted: e.counted !== false,
    };
  });
}

/** A Date as a datetime-local field value: the viewer's own time, to the minute. */
export function toLocalInput(date) {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The credit form, checked before anything is sent — the same rules the API enforces, said the way
 * the person typing needs to hear them. Returns the payload only when there is nothing to fix.
 */
export function validateCreditForm({ kind = 'top_up', amount = '', when = '', note = '' } = {}, now = new Date()) {
  const errors = {};

  const raw = String(amount).replace(/[₹,\s]/g, '');
  const value = raw === '' ? Number.NaN : Number(raw);
  if (!Number.isFinite(value)) errors.amount = 'Enter an amount';
  else if (Math.abs(value * 100 - Math.round(value * 100)) > 1e-6) errors.amount = 'At most two decimals';
  else if (Math.abs(value) > MAX_CREDIT_INR) errors.amount = `At most ${formatInr(MAX_CREDIT_INR)}`;
  else if (kind === 'adjustment') {
    if (Math.round(value * 100) === 0) errors.amount = 'An adjustment cannot be zero';
  } else if (value <= 0) errors.amount = 'Must be more than ₹0';

  const at = when ? new Date(when) : null;
  if (!at || Number.isNaN(at.getTime())) errors.when = 'Enter a date and time';
  else if (at.getTime() > now.getTime()) errors.when = 'Cannot be in the future';

  const trimmed = String(note ?? '').trim();
  if (trimmed.length > 200) errors.note = 'At most 200 characters';

  const clean = Object.keys(errors).length === 0;
  return {
    errors,
    payload: clean ? { kind, amount_inr: Math.round(value * 100) / 100, effective_at: at.toISOString(), note: trimmed || null } : null,
  };
}

/** How calls recorded before the source was kept were attributed — said once, under the source split. */
export function inferredNote(bySource) {
  const list = bySource ?? [];
  const inferred = list.reduce((n, x) => n + (x.inferred_calls || 0), 0);
  if (!inferred) return null;
  const earlier = list.some((x) => x.source === 'earlier' && x.calls > 0);
  return (
    `Calls recorded before the source was kept (${num(inferred)}) are attributed by what they were: a call for a vendor came from a ` +
    `verification run, a registry lookup without one from the registration form.` +
    (earlier ? ' Older document reads could be either, so they show as Earlier calls.' : '')
  );
}
